<template>
  <div :class="['flex flex-col mx-auto w-full max-w-screen-2xl', layout]">
    <template v-for="(item, index) in content.value">
      <div
        v-if="item.text"
        :key="index"
        :class="['prose prose-lg max-w-full lg:max-w-screen-2xl', textStyle(item.text)]"
        v-html="htmlText(item.text.content)"
      ></div>
      <div v-if="item.br === null" :key="index" class="h-4" />
      <div v-if="item.hr === null" :key="index" class="w-full py-4">
        <hr />
      </div>
      <div v-if="item.image" :key="index" :class="imageLayout(item.image)">
        <img
          v-bind="disalbeCopy"
          :src="webpImage(item.image.path)"
          :class="['max-w-full my-4', imageStyle(item.image)]"
        />
      </div>
      <div v-if="item.radio" :key="index" class="survey">
        <Choice
          :data="{
            $kind: 'Radio',
            ...item.radio
          }"
          :index="index"
          :isSubmit="isSubmit"
          :userAnswer="getUserAnswer(index, submitData.data)"
          class="w-full"
          @addField="handleAddField"
          @fieldChange="handleCheckField"
        />
      </div>
      <div v-if="item.checkbox" :key="index" class="survey">
        <Choice
          :data="{
            $kind: 'Checkbox',
            ...item.checkbox
          }"
          :index="index"
          :isSubmit="isSubmit"
          :userAnswer="getUserAnswer(index, submitData.data)"
          class="w-full"
          @addField="handleAddField"
          @fieldChange="handleCheckField"
        />
      </div>
      <div v-if="item.shortAnswer" :key="index" class="survey">
        <ShortAnswer
          :data="item.shortAnswer"
          :index="index"
          :isSubmit="isSubmit"
          :userAnswer="getUserAnswer(index, submitData.data)"
          class="w-full"
          @addField="handleAddField"
          @fieldChange="handleCheckField"
        />
      </div>
      <div v-if="item.reviewSurvey" :key="index">
        <ReviewSurvey
          ref="reviewSurvey"
          :isSubmit="isSubmit"
          :content="{
            ...content,
            ...item.reviewSurvey
          }"
          :step="step"
          :index="index"
          @addField="handleAddReview"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import showdown from 'showdown';
import { webpImage } from '@forchange/cleword-utils';

import Choice from '../survey/Choice';
import ShortAnswer from '../survey/ShortAnswer';
import ReviewSurvey from '../review-survey/index.vue';
import { Content } from '@forchange/storytime';
import { baseSurvey, SurveySaveData } from '../../survey/baseSurvey';

export type baseReview = {
  checkSaveing: () => boolean;
};

@Component({
  components: {
    Choice,
    ShortAnswer,
    ReviewSurvey
  }
})
export default class StepContent extends Vue {
  @Prop({ type: Object, default: () => {} }) content!: Content & { value: Array<any> };
  @Prop({ type: Number }) step!: Number;
  @Prop({ type: Boolean }) isSubmit!: boolean;

  // survey
  fields: Array<Vue & baseSurvey> = [];
  submitDisabled = true;
  submitData: Array<SurveySaveData> = [];

  // review
  reviews: Array<Vue & baseReview> = [];

  get layout() {
    const values = this.content.value.filter((item) => {
      return !!item.layout;
    });
    if (!values[0]) return '';
    return this.markLayout(values[0].layout);
  }

  get disalbeCopy() {
    if (this.$store.state.common.disalbeCopy) {
      return {
        style:
          'user-select: none;-webkit-user-drag: none;-webkit-user-select: none;-webkit-touch-callout: none;',
        oncontextmenu: 'return false;'
      };
    }
    return {};
  }

  webpImage(url: string) {
    return webpImage(url);
  }

  textStyle(text: { weight: number; size: number; align: string }) {
    const classArr = [];
    const fontWeight = [
      '',
      'font-thin',
      'font-extralight',
      'font-light',
      'font-normal',
      'font-medium',
      'font-semibold',
      'font-bold'
    ];
    const fontSize = [
      '',
      'text-xs',
      'text-sm',
      'text-base',
      'text-lg',
      'text-xl',
      'text-2xl',
      'text-3xl'
    ];

    if (text.weight) {
      classArr.push(fontWeight[text.weight]);
    }

    if (text.size) {
      classArr.push(fontSize[text.size]);
    }

    switch (text.align) {
      case '居左':
      case 'left':
        classArr.push('self-start');
        break;
      case '居中':
      case 'center':
        classArr.push('self-center');
        break;
      case '居右':
      case 'right':
        classArr.push('self-end');
        break;
      default:
        classArr.push('self-start');
    }

    return classArr.join(' ');
  }

  imageLayout(image: { align: string }) {
    const classArr = [];
    switch (image.align) {
      case '居左':
      case 'left':
        classArr.push('self-start');
        break;
      case '居中':
      case 'center':
        classArr.push('self-center');
        break;
      case '居右':
      case 'right':
        classArr.push('self-end');
        break;
      default:
        classArr.push('self-start');
    }
    return classArr.join(' ');
  }

  imageStyle(image: { size: number }) {
    const fontSize = ['', 'w-16', 'w-32', 'w-64', 'w-96', 'w-128', 'w-160', 'w-200'];
    return fontSize[image.size] || '';
  }

  markLayout(layout: { xAxis: string; yAxis: string; width: string }) {
    const classArr = [];

    switch (layout.xAxis) {
      case '居左':
      case 'left':
        classArr.push('items-start');
        break;
      case '居中':
      case 'center':
        classArr.push('items-center');
        break;
      case '居右':
      case 'right':
        classArr.push('items-end');
        break;
      default:
        classArr.push('items-start');
    }

    switch (layout.yAxis) {
      case '顶部':
      case 'top':
        classArr.push('justify-start');
        break;
      case '居中':
      case 'center':
        classArr.push('justify-center');
        break;
      case '底部':
      case 'bottom':
        classArr.push('justify-end');
        break;
      default:
        classArr.push('justify-start');
    }

    // width
    switch (layout.width) {
      case '大':
      case 'large':
        classArr.push('lg:w-11/12');
        break;
      case '中':
      case 'middle':
        classArr.push('lg:w-8/12');
        break;
      case '小':
      case 'small':
        classArr.push('lg:w-6/12');
        break;
      default:
        classArr.push('lg:w-10/12');
    }

    return classArr.join(' ');
  }

  mounted() {
    this.$nextTick(async () => {
      this.fields.forEach(async (field) => {
        const mark = field.getMark();
        const reviewData = await this.$store.dispatch('survey/getReviewData', {
          contentId: this.content.$id.split('#')[0],
          step: this.step,
          mark
        });
        if (reviewData && reviewData[0]?.content) field.updateUserAnswer(reviewData[0]?.content);
      });
    });
  }

  htmlText(value: string): string {
    const converter = new showdown.Converter({
      simpleLineBreaks: true,
      openLinksInNewWindow: true
    });
    return converter.makeHtml(value);
  }

  // survey
  getUserAnswer(index: number, submitDataList: Array<{ index: number }> | undefined) {
    const targetArray = submitDataList?.filter((item) => item.index === index);
    if (targetArray?.length) {
      return targetArray[0];
    }
    return;
  }

  handleAddField(field: Array<Vue & baseSurvey>) {
    if (field) {
      this.fields.push(...field);
    }
  }

  handleAddReview(review: Array<Vue & baseReview>) {
    if (review) {
      this.reviews.push(...review);
    }
  }

  handleCheckField() {
    return this.checkFields();
  }

  checkFields() {
    return this.fields.filter((item) => {
      return (item as Vue & { checkEmpty: Function }).checkEmpty();
    });
  }

  checkReviewSave() {
    return this.reviews.filter((item) => {
      return item.checkSaveing();
    });
  }

  changeReviewSurvey() {
    if (Array.isArray(this.$refs.reviewSurvey)) {
      (this.$refs.reviewSurvey as Array<Vue & { changeText: Function }>).forEach((item) => {
        item.changeText && item.changeText();
      });
    }
    this.fields.forEach((item) => {
      item.changeText && item.changeText();
    });
  }

  // 存储题目答案
  async saveAnswer(step: number) {
    for (const item of this.fields) {
      const mark = item.getMark();
      // 没有填写mark直接忽略
      if (!mark) return;
      await this.$store.dispatch('survey/setData', {
        contentId: this.content.$id.split('#')[0],
        content: item.getValue(),
        step,
        mark
      });
    }
  }
}
</script>
<style scoped>
.image-max {
  max-height: 80vh;
}
.survey {
  @apply w-full mt-8;
}
</style>
